import { forwardRef } from "react";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import classes from "./SPFinderFilters.module.less";
import type { Option } from "../SearchCard/SalesPartnerSearchBox";
import { useTranslation } from "react-i18next";
import { C_ON_SURFACE_LOW_EMPHASIS, C_SURFACE } from "~/common/components/icon/Colors";

export interface SPFinderFiltersRef {
  reset: () => void;
}

interface SPFinderFiltersProps {
  productCompetencesOptions?: Option[];
  certifiedCompetencesOptions?: Option[];
  selectedProductKeys: string[];
  selectedServiceKeys: string[];
  handleProductChange: SelectHandler;
  handleServiceChange: SelectHandler;
  getLabelByKey: GetLabelByKeyFn;
}

type SelectHandler = (event: SelectChangeEvent<string[]>) => void;

type GetLabelByKeyFn = (key: string, options: Option[]) => string;

const sharedMenuProps = {
  PaperProps: {
    sx: {
      "& .MuiMenuItem-root": {
        backgroundColor: C_SURFACE,
      },
      "& ul": {
        padding: 0,
        margin: 0,
      },
    },
  },
};

const SPFinderFilters = forwardRef<SPFinderFiltersRef, SPFinderFiltersProps>((props, ref) => {
  const { t } = useTranslation();

  const {
    productCompetencesOptions = [],
    certifiedCompetencesOptions = [],
    selectedProductKeys,
    selectedServiceKeys,
    handleProductChange,
    handleServiceChange,
    getLabelByKey,
  } = props;

  return (
    <div className={classes.extraFilters}>
      <div className={classes.extraFilter}>
        <p>{t("partnerFinder.search.productFilter")}</p>
        <Select
          size="small"
          multiple
          displayEmpty
          value={selectedProductKeys}
          onChange={handleProductChange}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return t("partnerFinder.search.productFilterPlaceholder");
            }
            return selected.map((key) => getLabelByKey(key, productCompetencesOptions)).join(", ");
          }}
          className={classes.filter}
          MenuProps={sharedMenuProps}
        >
          {productCompetencesOptions.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              <ListItemText primary={label} />
              <Checkbox checked={selectedProductKeys.includes(key)} />
            </MenuItem>
          ))}
        </Select>
      </div>
      <div className={classes.extraFilter}>
        <p>{t("partnerFinder.search.serviceFilter")}</p>
        <Select
          size="small"
          multiple
          displayEmpty
          value={selectedServiceKeys}
          onChange={handleServiceChange}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return t("partnerFinder.search.serviceFilterPlaceholder");
            }
            return selected.map((key) => getLabelByKey(key, certifiedCompetencesOptions)).join(", ");
          }}
          className={classes.filter}
          MenuProps={sharedMenuProps}
        >
          {certifiedCompetencesOptions.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              <ListItemText primary={label} />
              <Checkbox checked={selectedServiceKeys.includes(key)} />
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );
});

export default SPFinderFilters;
